import React, { useEffect, useState, Suspense, lazy } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import Catalog from "./pages/Catalog";
import Sidebar from "./components/Sidebar";
import { ThemeProvider } from "@mui/material/styles";
import { CssBaseline } from "@mui/material";
import { grey, blueGrey } from "@mui/material/colors";
import { createTheme, responsiveFontSizes } from "@mui/material";
import { Toaster } from "react-hot-toast";
import PageViewTracker from "./components/PageViewTracker";

// Lazy-loaded pages — only downloaded when the user navigates to them
const FAQ = lazy(() => import("./pages/FAQ"));
const SquatPage = lazy(() => import("./pages/exercises/SquatPage"));
const Program = lazy(() => import("./pages/Program"));
const ProgramOverlay = lazy(() => import("./pages/ProgramOverlay"));
const ExercisePage = lazy(() => import("./pages/exercises/ExercisePage"));
const MyExerSights = lazy(() => import("./pages/MyExerSights"));
const PushUpGamePage = lazy(() => import("./utils/exercises/pushUpGame"));
const SquatGamePage = lazy(() => import("./utils/exercises/squatGame"));

// SEO / Resources pages (lazy-loaded)
const ResourcesHub = lazy(() => import("./pages/resources/ResourcesHub"));
const ShoulderROMPage = lazy(() => import("./pages/resources/ShoulderROMPage"));
const CPT97530Page = lazy(() => import("./pages/resources/CPT97530Page"));
const MovementAnalysisPage = lazy(() => import("./pages/resources/MovementAnalysisPage"));
const PoseEstimationPage = lazy(() => import("./pages/resources/PoseEstimationPage"));
const ExerciseIndexPage = lazy(() => import("./pages/resources/ExerciseIndexPage"));
const ExerciseSEOPage = lazy(() => import("./pages/resources/ExerciseSEOPage"));
const DigitalGoniometerPage = lazy(() => import("./pages/resources/DigitalGoniometerPage"));
const TelerehabilitationPage = lazy(() => import("./pages/resources/TelerehabilitationPage"));
const AIExerciseFormCheckerPage = lazy(() => import("./pages/resources/AIExerciseFormCheckerPage"));
const KneeROMPage = lazy(() => import("./pages/resources/KneeROMPage"));
const TypesOfROMPage = lazy(() => import("./pages/resources/TypesOfROMPage"));
const CPT97530vs97110Page = lazy(() => import("./pages/resources/CPT97530vs97110Page"));
const MarkerlessMotionCapturePage = lazy(() => import("./pages/resources/MarkerlessMotionCapturePage"));


/**
 * The root component of the application, managing routing between different pages.
 * It uses `react-router-dom` to navigate between the main pages and specific exercise pages.
 * The Menubar component is displayed across all routes.
 *
 * @component
 * @returns {JSX.Element} The main app component with routing and navigation.
 */
function App() {
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem("darkMode") === "true";
  });

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  useEffect(() => {
    localStorage.setItem("darkMode", darkMode);
    // Sync DaisyUI theme with dark mode
    document.documentElement.setAttribute(
      "data-theme",
      darkMode ? "weguide_dark" : "weguide"
    );
  }, [darkMode]);

  let theme = createTheme({
    palette: {
      mode: darkMode ? "dark" : "light",
      primary: {
        main: darkMode ? "#9061F0" : "#7133EA",
        contrastText: "#ffffff",
      },
      secondary: {
        main: darkMode ? "#2a2a3e" : "#F4F4F5",
        contrastText: darkMode ? "#e5e7eb" : "#374151",
      },
      background: {
        default: darkMode ? "#1a1a2e" : "#F4F4F4",
        paper: darkMode ? "#16213e" : "#ffffff",
      },
      text: {
        primary: darkMode ? grey[50] : "#374151",
        secondary: darkMode ? blueGrey[100] : "#6b7280",
      },
      success: {
        main: "#16a34a",
      },
      info: {
        main: darkMode ? "#3b82f6" : "#2563eb",
      },
      error: {
        main: "#EB504B",
      },
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
      },
    },
    typography: {
      fontFamily: "Poppins, Lexend, Arial, sans-serif",
      h1: {
        fontSize: "3.125rem",
        fontWeight: 600,
        lineHeight: 1.2,
        xs: {
          fontSize: "4.125rem",
        },
        md: {
          fontSize: "5.125rem",
        },
      },
      h2: {
        fontSize: "2.625rem",
        fontWeight: 500,
        sm: {
          fontSize: "3.625rem",
        },
        md: {
          fontSize: "4.625rem",
        },
      },
      h3: {
        fontSize: "2.125rem",
        fontWeight: 500,
        lineHeight: 1.3,
        sm: {
          fontSize: "2.625rem",
        },
        md: {
          fontSize: "3.125rem",
        },
      },
      h4: {
        fontSize: "1.8125rem",
        fontWeight: 500,
        lineHeight: 1.4,
        sm: {
          fontSize: "2.125rem",
        },
        md: {
          fontSize: "2.625rem",
        },
      },
      h5: {
        fontSize: "1.5625rem",
        fontWeight: 500,
        lineHeight: 1.4,
        sm: {
          fontSize: "1.8125rem",
        },
        md: {
          fontSize: "2.125rem",
        },
      },
      h6: {
        fontSize: "1.3125rem",
        fontWeight: 500,
        lineHeight: 1.4,
        sm: {
          fontSize: "1.5625rem",
        },
        md: {
          fontSize: "1.8125rem",
        },
      },
      subtitle1: {
        fontSize: "1.15625rem",
        fontWeight: 500,
        lineHeight: 1.5,
        sm: {
          fontSize: "1.3125rem",
        },
        md: {
          fontSize: "1.5625rem",
        },
      },
      subtitle2: {
        fontSize: "1.03125rem",
        fontWeight: 500,
        lineHeight: 1.5,
        sm: {
          fontSize: "1.15625rem",
        },
        md: {
          fontSize: "1.3125rem",
        },
      },
      body1: {
        fontSize: "1.03125rem",
        lineHeight: 1.5,
        sm: {
          fontSize: "1.15625rem",
        },
        md: {
          fontSize: "1.3125rem",
        },
      },
      body2: {
        fontSize: "0.90625rem",
        lineHeight: 1.43,
        sm: {
          fontSize: "1.03125rem",
        },
      },
      button: {
        fontSize: "0.9875rem",
        fontWeight: 500,
        lineHeight: 1.75,
        textTransform: "uppercase",
      },
      caption: {
        fontSize: "0.78125rem",
        lineHeight: 1.66,
        fontWeight: 400,
      },
      overline: {
        fontSize: "0.78125rem",
        fontWeight: 400,
        lineHeight: 2.66,
        textTransform: "uppercase",
      },
    },
  });

  theme = responsiveFontSizes(theme);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Toaster
          position="top-right"
          containerStyle={{
            top: 20,
          }}
        />
        <Router>
          <PageViewTracker />
          {/* <LoginPrompt /> -- login disabled for now */}
          <div className="app-layout">
            <Sidebar darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
            <main className="app-main">
              <Suspense fallback={<div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}><span className="loading loading-spinner loading-lg"></span></div>}>
              <Routes>
                <Route path="/" element={<Catalog />} />
                <Route path="/catalog" element={<Navigate to="/" replace />} />
                <Route path="/about" element={<Navigate to="/faq" replace />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/home" element={<Navigate to="/" replace />} />
                <Route path="/squat" element={<SquatPage />} />
                <Route path="/exercise" element={<ExercisePage />} />
                <Route path="/program" element={<Program />} />
                <Route path="/programOverlay" element={<ProgramOverlay />} />
                <Route path="/myAssessments" element={<MyExerSights />} />
                <Route path="/myExerSights" element={<MyExerSights />} />
                <Route path="/pushUpGame" element={<PushUpGamePage />} />
                <Route path="/squatGame" element={<SquatGamePage />} />

                {/* SEO / Resources pages */}
                <Route path="/resources" element={<ResourcesHub />} />
                <Route path="/resources/shoulder-range-of-motion" element={<ShoulderROMPage />} />
                <Route path="/resources/cpt-97530-therapeutic-activity" element={<CPT97530Page />} />
                <Route path="/resources/movement-analysis" element={<MovementAnalysisPage />} />
                <Route path="/resources/pose-estimation" element={<PoseEstimationPage />} />
                <Route path="/resources/digital-goniometer" element={<DigitalGoniometerPage />} />
                <Route path="/resources/telerehabilitation" element={<TelerehabilitationPage />} />
                <Route path="/resources/ai-exercise-form-checker" element={<AIExerciseFormCheckerPage />} />
                <Route path="/resources/knee-range-of-motion" element={<KneeROMPage />} />
                <Route path="/resources/types-of-range-of-motion" element={<TypesOfROMPage />} />
                <Route path="/resources/cpt-97530-vs-97110" element={<CPT97530vs97110Page />} />
                <Route path="/resources/markerless-motion-capture" element={<MarkerlessMotionCapturePage />} />
                <Route path="/resources/exercises" element={<ExerciseIndexPage />} />
                <Route path="/resources/exercises/:exerciseName" element={<ExerciseSEOPage />} />
              </Routes>
              </Suspense>
            </main>
          </div>
        </Router>
    </ThemeProvider>
  );
}

export default App;
